import React, { ReactNode, useEffect, useMemo, useReducer, useState } from 'react';
import Modal from '@components/Modals';
import cn from 'classnames';
import Header from '@components/Modals/Header';
import styles from './CSV.module.scss';
import DragDropFileOpener from '@components/DragAndDropFile/DragAndDropFile';
import type { ParseResult } from 'papaparse';
import Papa from 'papaparse';
import { ChevronLeftIcon, ChevronRightIcon, CircledIIcon, GreenCheckMarkIcon } from '@components/Icons';
import Action from '@components/Button/Action';

const csvImage = '/img/settings/csv-dark.png';
const csvImageLight = '/img/settings/csv-light.png';

import * as chrono from 'chrono-node';

import validEmail from '@support/valid-email';
import validURL from '@support/valid-url';
import SVGWrapper from '@components/Icons/SVGWrapper';
import { SettingsInlineButton } from '@views/Settings/components';
import { HighAlertIcon, MedAlertIcon } from '@components/Icons/AlertIcons';
import Tooltip, { Overlay } from '@components/Tooltip';
import Image from '@components/Image';
import { GroupPill } from '@components/Contact/Groups';
import Group from '@models/Group';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { createCSVImport, DateParts, ImportablePerson } from '@actions/csvImport';
import { CadenceType } from '@models/Cadence';
import { useDispatch } from 'react-redux';
import { localizeNumber } from '@support/number';

const supportedDelimiters = [',', ';', '\n', '\t', ':::'];

/*
TODO:
- ignore _warnings and _error columns when importing
*/

interface CSVRecord {
  // Lookup fields. Need to have at least one of these (or both First Name and Last Name together)
  'name'?: string;
  'first name'?: string;
  'last name'?: string;
  'email'?: string;
  'phone'?: string;
  'linkedin url'?: string;
  'facebook url'?: string;
  'twitter url'?: string;

  'note'?: string;
  'groups'?: string;
  'birthday'?: string;
  'title'?: string;
  'company': string;
  'website'?: string;
  'headline'?: string;
  'cadence'?: string;
  // Address fields
  'address - street'?: string;
  'address - city'?: string;
  'address - state'?: string;
  'address - postal code'?: string;
  'address - country'?: string;
}

type CSVRecordWithErrors = CSVRecord & { _error?: string; _warnings?: string };

// interface DateParts {
//   year?: number;
//   month: number;
//   day: number;
// }

// type Cadence = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'auto'; // or whatever Dex supports
// interface WorkExperience { title?: string; company?: string };
// interface Address { street?: string; city?: string; state?: string; postal_code?: string; country?: string };

// interface ImportablePerson {
//   // Lookup fields. Need to have at least one of these (or both First Name and Last Name together) for a valid row
//   'name'?: string;
//   'first_name'?: string;
//   'last_name'?: string;
//   'email_addresses'?: string[];
//   'phone_numbers'?: string[];
//   'linkedin_urls'?: string[];
//   'facebook_urls'?: string[];
//   'twitter_urls'?: string[];

//   'notes'?: string[];
//   'groups'?: string[];
//   'birthday'?: DateParts;
//   'work_history'?: WorkExperience[];
//   'websites'?: string[];
//   'headline'?: string;
//   'cadence'?: Cadence;
//   'addresses'?: Address[];
// }

type CSVField = keyof CSVRecord;

// field names
// const NAME = 'name';
// const FIRST_NAME = 'first name';
// const LAST_NAME = 'last name';
// const EMAIL = 'email';
// const PHONE = 'phone';
// const LINKEDIN_URL = 'linkedin url';
// const FACEBOOK_URL = 'facebook url';
// const TWITTER_URL = 'twitter url';
// const NOTE = 'note';
// const GROUPS = 'groups';
// const BIRTHDAY = 'birthday';
// const TITLE = 'title';
// const COMPANY = 'company';
// const WEBSITE = 'website';
// const HEADLINE = 'headline';
// const CADENCE = 'cadence';
// const ADDRESS_STREET = 'address - street';
// const ADDRESS_CITY = 'address - city';
// const ADDRESS_STATE = 'address - state';
// const ADDRESS_POSTAL_CODE = 'address - postal code';
// const ADDRESS_COUNTRY = 'address - country';

const PRETTY_NAME = 'Name';
const PRETTY_FIRST_NAME = 'First Name';
const PRETTY_LAST_NAME = 'Last Name';
const PRETTY_EMAIL = 'Email';
const PRETTY_PHONE = 'Phone';
const PRETTY_LINKEDIN_URL = 'LinkedIn URL';
const PRETTY_FACEBOOK_URL = 'Facebook URL';
const PRETTY_TWITTER_URL = 'Twitter URL';
// const PRETTY_NOTE = 'Note';
// const PRETTY_GROUPS = 'Groups';
// const PRETTY_BIRTHDAY = 'Birthday';
// const PRETTY_TITLE = 'Title';
// const PRETTY_COMPANY = 'Company';
// const PRETTY_WEBSITE = 'Website';
// const PRETTY_HEADLINE = 'Headline';
// const PRETTY_CADENCE = 'Cadence';
// const PRETTY_ADDRESS_STREET = 'Address - Street';
// const PRETTY_ADDRESS_CITY = 'Address - City';
// const PRETTY_ADDRESS_STATE = 'Address - State';
// const PRETTY_ADDRESS_POSTAL_CODE = 'Address - Postal Code';
// const PRETTY_ADDRESS_COUNTRY = 'Address - Country';

const NAME = 'name';
const FIRST_NAME = 'first name';
const LAST_NAME = 'last name';
const EMAIL = 'email';
const PHONE = 'phone';
const LINKEDIN_URL = 'linkedin url';
const FACEBOOK_URL = 'facebook url';
const TWITTER_URL = 'twitter url';
const NOTE = 'note';
const GROUPS = 'groups';
const BIRTHDAY = 'birthday';
const TITLE = 'title';
const COMPANY = 'company';
const WEBSITE = 'website';
const HEADLINE = 'headline';
const CADENCE = 'cadence';
const ADDRESS_STREET = 'address - street';
const ADDRESS_CITY = 'address - city';
const ADDRESS_STATE = 'address - state';
const ADDRESS_POSTAL_CODE = 'address - postal code';
const ADDRESS_COUNTRY = 'address - country';


const supportedFields = [
  NAME,
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  PHONE,
  LINKEDIN_URL,
  FACEBOOK_URL,
  TWITTER_URL,
  NOTE,
  GROUPS,
  BIRTHDAY,
  TITLE,
  COMPANY,
  WEBSITE,
  HEADLINE,
  CADENCE,
  ADDRESS_STREET,
  ADDRESS_CITY,
  ADDRESS_STATE,
  ADDRESS_POSTAL_CODE,
  ADDRESS_COUNTRY,
];

const CLAY_ERROR = '_error';
const CLAY_WARNINGS = '_warnings';

const clayAddedFields = [CLAY_ERROR, CLAY_WARNINGS];



interface ValidationResult {
  valid: boolean;
  error?: string;
  // warning?: string;

  sanitizedValue?: string | string[] | DateParts;
}

const validPhoneNumber = (phoneNumber: string): boolean => /^[\d\s\-\(\)+x]+$/.test(phoneNumber);

const hasValueValidator = (value: string): ValidationResult => ({ valid: !!value });

type ValidatorFn = (value: string | string[]) => ValidationResult;

const emailValidator = (emails: string[]): ValidationResult => {
  if (emails.length === 0) {
    return { valid: false };
  }

  const validEmails = emails.filter(validEmail);

  return {
    valid: validEmails.length > 0,
    error: validEmails.length !== emails.length ? 'Some emails are invalid' : undefined,
    sanitizedValue: validEmails,
  };
};

const phoneValidator = (phoneNumbers: string[]): ValidationResult => {
  if (phoneNumbers.length === 0) {
    return { valid: false };
  }

  const validPhoneNumbers = phoneNumbers.filter(validPhoneNumber);
  const invalidPhoneNumberCount = phoneNumbers.length - validPhoneNumbers.length;
  const errorMsg = `${invalidPhoneNumberCount} invalid phone number${
    invalidPhoneNumberCount > 1 ? 's' : ''
  }`;

  return {
    valid: validPhoneNumbers.length > 0,
    error: invalidPhoneNumberCount > 0 ? errorMsg : undefined,
    sanitizedValue: validPhoneNumbers,
  };
};

const urlValidator =
  (domains?: string[]) =>
  (url: string): ValidationResult => {
    if (!url) {
      return { valid: false };
    }

    if (!domains) {
      const valid = validURL(url);
      return { valid, error: valid ? undefined : 'Invalid URL' };
    }

    const valid = validURL(url) && domains.some((domain) => url.includes(domain));
    return {
      valid,
      error: valid ? undefined : `Invalid URL. Must include ${domains.join(' or ')}`,
    };
  };

const groupsValidator = (groups: string[]): ValidationResult => {
  if (groups.length === 0) {
    return { valid: false };
  }

  return { valid: true };
};

// Support yyyy-mm-dd, mm-dd
const dateValidator = (date: string): ValidationResult => {
  if (!date) {
    return { valid: false };
  }

  if (date.includes('-')) {
    // Use Keith's existing logic (roughly) for date parsing for google contact imports
    const transformedDate = date.replaceAll('--', '0-');
    const parts = transformedDate.split('-');
    let year = undefined;
    let month = Number(parts[0]);
    let day = Number(parts[1]);
    
    if (parts.length === 3) {
      year = Number(parts[0]);
      month = Number(parts[1]);
      day = Number(parts[2]);
    }

    if (isNaN(month) || isNaN(day)) {
      return { valid: false, error: 'Invalid date' };
    }

    return {
      valid: true,
      sanitizedValue: {
        year,
        month,
        day,
      }
    }
  }

  const parts = chrono.parse(date);

  if (!parts || parts.length === 0 || !parts[0]?.start) {
    return { valid: false, error: 'Invalid date' };
  }

  const parsedDate = parts[0].start;

  let year = undefined;
  if (parsedDate.isCertain('year')) {
    year = parsedDate.get('year');
  }

  if (!parsedDate.isCertain('month') || !parsedDate.isCertain('day')) {
    return { valid: false, error: 'Invalid date' };
  }

  return {
    valid: true,
    sanitizedValue: {
      year,
      month: parsedDate.get('month'),
      day: parsedDate.get('day'),
    },
  };
};

const toCadence = (cadence: string): CadenceType => {
  switch (cadence) {
    case 'daily':
      return 'daily';

    case 'weekly':
    case 'every week':
    case '7 days':
    case 'every 2 weeks':
    case '14 days':
      return 'weekly';

    case 'monthly':
    case 'every month':
    case '1 mon':
      return 'monthly';

    case 'quarterly':
    case 'every quarter':
    case 'every 3 months':
    case '3 mons':
      return 'quarterly';

    case 'yearly':
    case 'every 6 months':
    case '5 mons':
    case 'every year':
    case '1 year':
      return 'yearly';

    case 'auto':
      return 'auto';
  
    default:
      return undefined;
  }
}

const cadenceValidator = (cadence: string): ValidationResult => {
  const parsedCadence = toCadence(String(cadence).toLowerCase());

  if (!parsedCadence) {
    return { valid: false, error: !!cadence ? `"${cadence}" is not a valid Cadence. Accepted values: "daily" | "weekly" | "monthly" | "quarterly" | "yearly"` : undefined };
  } else {
    return { valid: true, sanitizedValue: parsedCadence };
  }
}

// const validationToArray = (validator: (value: string) => ValidationResult):  => (

const arrayifyValidation = (validator: ValidatorFn): ValidatorFn => {
  return (value: string | string[]): ValidationResult => {
    const validation = validator(value);

    if (validation.valid) {
      return {
        ...validation,
        sanitizedValue: Array.isArray(value) ? value : [value],
      }
    }

    return validation;
  };
}

const validators: Record<CSVField, ValidatorFn> = {
  [NAME]: hasValueValidator,
  [FIRST_NAME]: hasValueValidator,
  [LAST_NAME]: hasValueValidator,
  [EMAIL]: emailValidator,
  [PHONE]: phoneValidator,
  [LINKEDIN_URL]: arrayifyValidation(urlValidator(['linkedin.com/in/'])),
  [FACEBOOK_URL]: arrayifyValidation(urlValidator(['facebook.com/'])),
  [TWITTER_URL]: arrayifyValidation(urlValidator(['twitter.com/', 'x.com/'])),
  [WEBSITE]: arrayifyValidation(urlValidator()),

  [GROUPS]: groupsValidator,
  [NOTE]: hasValueValidator,
  [TITLE]: hasValueValidator,
  [COMPANY]: hasValueValidator,
  [HEADLINE]: hasValueValidator,

  [ADDRESS_STREET]: hasValueValidator,
  [ADDRESS_CITY]: hasValueValidator,
  [ADDRESS_STATE]: hasValueValidator,
  [ADDRESS_POSTAL_CODE]: hasValueValidator, // validPostalCode?
  [ADDRESS_COUNTRY]: hasValueValidator,

  [BIRTHDAY]: dateValidator,
  [CADENCE]: cadenceValidator,
};

const headerToKey: Record<CSVField, string> = {
  [NAME]: 'name',
  [FIRST_NAME]: 'first_name',
  [LAST_NAME]: 'last_name',
  [EMAIL]: 'email_addresses',
  [PHONE]: 'phone_numbers',
  [LINKEDIN_URL]: 'linkedin_urls',
  [FACEBOOK_URL]: 'facebook_urls',
  [TWITTER_URL]: 'twitter_urls',
  [NOTE]: 'notes',
  [GROUPS]: 'groups',
  [BIRTHDAY]: 'birthday',
  [TITLE]: 'work_history.title',
  [COMPANY]: 'work_history.company',
  [WEBSITE]: 'websites',
  [HEADLINE]: 'headline',
  [CADENCE]: 'cadence',
  [ADDRESS_STREET]: 'addresses.street',
  [ADDRESS_CITY]: 'addresses.city',
  [ADDRESS_STATE]: 'addresses.state',
  [ADDRESS_POSTAL_CODE]: 'addresses.postal_code',
  [ADDRESS_COUNTRY]: 'addresses.country',
};


const includesLookup = (mixedCharCols: string[]): boolean => {
  const cols = mixedCharCols.map((col) => col.toLowerCase());

  return (
    cols.includes(NAME) ||
    cols.includes(EMAIL) ||
    cols.includes(PHONE) ||
    cols.includes(LINKEDIN_URL) ||
    cols.includes(FACEBOOK_URL) ||
    cols.includes(TWITTER_URL) ||
    (cols.includes(FIRST_NAME) && cols.includes(LAST_NAME))
  );
}
  

function validateRow(record: CSVRecord): {
  importable: boolean;
  sanitizedRecord: ImportablePerson;
  recordWithErrors: CSVRecordWithErrors;
  groupNames: string[];
} {
  const sanitizedRecord = {} as ImportablePerson;
  const recordWithErrors: CSVRecordWithErrors = { ...record };
  let groupNames: string[] = [];

  const [validFields, columnErrors, unsupportedKVTuples] = Object.entries(record).reduce(
    ([validFields, columnErrors, unsupportedKVTuples], [userField, value]) => {
      const field = userField.toLowerCase().trim();

      if (!value || value?.length === 0) {
        return [validFields, columnErrors, unsupportedKVTuples];
      }

      if (!validators[field as CSVField]) {
        if (!!value && !clayAddedFields.includes(field)) {
          return [validFields, columnErrors, [...unsupportedKVTuples, [userField, value]]];
        }
        return [validFields, columnErrors, unsupportedKVTuples];
      }

      const result = validators[field as CSVField](value);

      if (!result.valid && result.error) {
        return [
          validFields,
          [...columnErrors, { field: userField, error: result.error }],
          unsupportedKVTuples,
        ];
      } else {
        const personKey = headerToKey[field as CSVField];

        if (personKey === 'groups') {
          groupNames = result.sanitizedValue || value;
        }

        if (personKey.includes('.')) {
          const [parentKey, propertyKey] = personKey.split('.');
          const existingValue = sanitizedRecord[parentKey]?.[0] || {};
          sanitizedRecord[parentKey] = [{ ...existingValue, [propertyKey]: result.sanitizedValue || value }];
        } else {
          sanitizedRecord[personKey] = result.sanitizedValue || value;
        }

        return [[...validFields, field], columnErrors, unsupportedKVTuples];
      }
    },
    [[], [], []],
  );

  // use includesLookup on the validFields to determine if the row is importable
  const importable = includesLookup(validFields);

  // if importable, append note with unsupported fields
  if (!importable) {
    recordWithErrors[CLAY_ERROR] =
      'Must include at least one of the following: Name, Email, Phone, LinkedIn URL, Facebook URL, Twitter URL, or First Name + Last Name';
  }

  if (columnErrors.length > 0) {
    recordWithErrors[CLAY_WARNINGS] = columnErrors
      .map(({ field, error }) => `${field}: ${error}`)
      .join(', ');
  }

  if (importable && unsupportedKVTuples.length > 0) {
    const rowNotes = sanitizedRecord[NOTE] || [];
    const unsupportedFields = unsupportedKVTuples
      .map(([field, value]) => `${field}: ${value}`)
      .join('\n');
    sanitizedRecord[NOTE] = [...rowNotes, unsupportedFields];
  }

  return {
    importable,
    sanitizedRecord,
    recordWithErrors,
    groupNames,
  };
}

async function openAndParseCSVFile(file: File): Promise<ParseResult<CSVRecord>> {
  const detectedDelimiter = {
    [EMAIL]: undefined,
    [PHONE]: undefined,
    [GROUPS]: undefined,
  };

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: resolve,
      error: reject,
      // 'greedy' skips lines that just have whitespace
      skipEmptyLines: 'greedy',
      transform: (value, userField: string) => {
        const field = userField.toLowerCase().trim();
        if ([EMAIL, PHONE, GROUPS].includes(field)) {
          if (!detectedDelimiter[field]) {
            detectedDelimiter[field] = supportedDelimiters.find((delimiter) =>
              value.includes(delimiter),
            );
          }

          let values = [];

          if (detectedDelimiter[field]) {
            values = value.split(detectedDelimiter[field]).map((v) => v.trim());
          } else {
            values = [value.trim()];
          }

          return values.filter((v) => !!v);
        }

        if ([NOTE].includes(field) && value) {
          return [value];
        }

        return String(value).trim();
      },
    });
  });
}

// STATE: { parsingError, sanitizedRecords, CSVWithErrors }

type CSVModalView = 'upload' | 'review' | 'import' | 'done';
type CSVModalViewState = 'working' | 'error' | 'done';

interface CSVModalState {
  userFile?: File;

  sanitizedRecords?: ImportablePerson[];
  CSVWithErrorStr?: string;

  erroredRowCount?: number;
  warnedRowCount?: number;
  detectedUnsupportedFields?: string[];
  hasImportableHeaders?: boolean;
  hasNoHeadersFromTemplate?: boolean;
  allGroupNames?: string[];

  fileSelectionError?: string;
  parsingError?: string;
  uploadError?: string;

  view: CSVModalView;
  viewState?: CSVModalViewState;
}

type CSVModalStateAction =
  | ['restart']
  | ['fileSelectionComplete', { file: File }]
  | ['fileSelectionError', { error: string }]
  | ['parsingStarted']
  | [
      'parsingComplete',
      {
        sanitizedRecords: ImportablePerson[];
        erroredRowCount: number;
        warnedRowCount: number;
        detectedUnsupportedFields: string[];
        CSVWithErrorStr?: string;
        hasImportableHeaders: boolean;
        hasNoHeadersFromTemplate: boolean;
        // hasNoImportableRows
        allGroupNames: string[];
      },
    ]
  | ['parsingError', { error: string }]
  | ['returnToReview']
  | ['readyToUpload']
  | ['uploadStarted']
  | ['uploadComplete']
  | ['uploadError', { error: string }];

const csvModalStateReducer = (state: CSVModalState, action: CSVModalStateAction): CSVModalState => {
  switch (action[0]) {
    case 'restart':
      return {
        ...state,
        view: 'upload',
        viewState: undefined,
      };

    case 'fileSelectionComplete':
      return {
        ...state,
        userFile: action[1].file,
        fileSelectionError: undefined,
        viewState: 'done',
      };

    case 'fileSelectionError':
      return {
        ...state,
        fileSelectionError: action[1].error,
        viewState: 'error',
      };

    case 'parsingStarted':
      return {
        ...state,
        view: 'review',
        parsingError: undefined,
        viewState: 'working',
      };

    case 'parsingComplete':
      return {
        ...state,
        sanitizedRecords: action[1].sanitizedRecords,
        erroredRowCount: action[1].erroredRowCount,
        warnedRowCount: action[1].warnedRowCount,
        detectedUnsupportedFields: action[1].detectedUnsupportedFields,
        CSVWithErrorStr: action[1].CSVWithErrorStr,
        hasImportableHeaders: action[1].hasImportableHeaders,
        hasNoHeadersFromTemplate: action[1].hasNoHeadersFromTemplate,
        allGroupNames: action[1].allGroupNames,
        viewState: 'done',
      };

    case 'parsingError':
      return {
        ...state,
        parsingError: action[1].error,
        viewState: 'error',
      };

    case 'returnToReview':
      return {
        ...state,
        view: 'review',
        viewState: 'done',
      };

    case 'readyToUpload':
      return {
        ...state,
        view: 'import',
        viewState: 'done',
      };

    case 'uploadStarted':
      return {
        ...state,
        view: 'done',
        uploadError: undefined,
        viewState: 'working',
      };

    case 'uploadComplete':
      return {
        ...state,
        viewState: 'done',
      };

    case 'uploadError':
      return {
        ...state,
        uploadError: action[1].error,
        viewState: 'error',
      };

    default:
      return state;
  }
};

const FileIcon = () => {
  return (
    <SVGWrapper viewBox="0 0 18 18" width="18" height="18" color="textPrimary">
      <path
        fill="none"
        strokeLinecap="round"
        strokeWidth="1.5"
        d="M6 2.25h3.96c.35 0 .69.12.96.35l3.54 2.95c.34.29.54.7.54 1.15v6.05a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-7.5a3 3 0 0 1 3-3ZM6 12.38h6M6 9h3M9.75 3.38v1.87c0 .83.67 1.5 1.5 1.5h3"
      />
    </SVGWrapper>
  );
};

const StatusIcon = ({ hasWarning, hasError }: { hasWarning?: boolean; hasError?: boolean }) => {
  if (hasError) {
    return <HighAlertIcon />;
  }

  if (hasWarning) {
    return <MedAlertIcon />;
  }

  return <GreenCheckMarkIcon />;
};

const rowWarningMsg = (warnedRowCount: number) => (
  <>
  {warnedRowCount} row{
    warnedRowCount > 1 ? 's have' : ' has'
  } some values that are<br/>not formatted correctly
  </>
);
const rowErrorMsg = (hasNoRows: boolean, erroredRowCount: number) => {
  if (hasNoRows) {
    return <>No valid rows were found.<br/>There is no contact data to import!</>
  }

  return (
    <>
    {erroredRowCount} row{
    erroredRowCount > 1 ? 's are' : ' is'
  } missing contact information<br/>and cannot be imported
  </>
  )
};

const columnMsg = (
  noViableHeaders: boolean,
  noImportableHeaders: boolean,
  unsupportedFields: string[],
) => {
  if (noViableHeaders) {
    return <>No supported column headers were detected</>
  }

  if (noImportableHeaders) {
    return <>No contact identifier column headers were detected</>;
  }

  if (unsupportedFields.length > 0) {
    return (
    <>
      Unsupported column headers detected
      <ul>
        {unsupportedFields.map((field) => (
          <li key={field}>{field}</li>
        ))}
      </ul>
    </>
    )
  }

  return '';
};

const DOCS_URL = '#';

const AlertBox = ({
  status,
  title,
  children,
}: {
  status: 'error' | 'warning';
  title: string;
  children: ReactNode;
}) => {
  return (
    <div className={cn(styles.box, status === 'error' ? styles.boxError : styles.boxWarning)}>
      {status === 'error' ? <HighAlertIcon /> : <MedAlertIcon />}
      <div className={styles.title}>{title}</div>
      <div className={styles.boxCopy}>{children}</div>
    </div>
  );
};

const UploadView = ({ selectFile, closeModal, upload, uploadDisabled, forceDarkMode }) => {
  return (
    <>
      <div className={styles.copy}>
        Import a list of people using a CSV file. Make sure to follow our{' '}
        <a href={DOCS_URL}>formatting guidelines</a>.
      </div>
      <DragDropFileOpener onFile={selectFile} />
      <div className={styles.actions}>
        <Action className={styles.modalBtn} onClick={closeModal} secondary forceDarkMode={forceDarkMode}>
          Cancel
        </Action>
        <Action className={styles.modalBtn}
          variant="primary"
          disabled={uploadDisabled}
          onClick={upload}
          forceDarkMode={forceDarkMode}
        >
          Upload
        </Action>
      </div>
    </>
  );
};

interface GeneralCSVErrors {
  row: boolean;
  column: boolean;
}

interface GeneralCSVWarnings {
  row: boolean;
  column: boolean;
}


const alertBoxTitle = (
  errors: GeneralCSVErrors,
  warnings: GeneralCSVWarnings,
  hasNoHeadersFromTemplate: boolean,
  hasImportableHeaders: boolean,
  hasNoRows: boolean,
  hasNoImportableRows: boolean,
) => {
  if (hasNoHeadersFromTemplate) {
    return 'No supported column headers'
  }

  if (!hasImportableHeaders) {
    return 'No identifier columns detected'
  }

  if (hasNoRows) {
    return 'CSV has no rows'
  }

  if (hasNoImportableRows) {
    return 'No importable rows'
  }

  if (errors.row) {
    return 'Some rows cannot be imported'
  }

  // handle warnings generically in title, specifically below in message
  if (warnings.row) {
    return 'Some data not properly formatted'
  }

  if (warnings.column) {
    return 'Unsupported column headers';
  }

  return 'Warning';
}

const PIIFields = `"${PRETTY_NAME}", "${PRETTY_EMAIL}", "${PRETTY_PHONE}", "${PRETTY_LINKEDIN_URL}", "${PRETTY_FACEBOOK_URL}", "${PRETTY_TWITTER_URL}", or both "${PRETTY_FIRST_NAME}" and "${PRETTY_LAST_NAME}"`

// TODO: handle column warning


const alertBoxMessage = (
  errors: GeneralCSVErrors,
  warnings: GeneralCSVWarnings,
  hasNoHeadersFromTemplate: boolean,
  hasImportableHeaders: boolean,
  hasNoRows: boolean,
  hasNoImportableRows: boolean,
  importableRowCount: number,
  erroredRowCount: number,
  warnedRowCount: number,
  errorCSV: string,
  errorCSVFileName: string,
) => {
  if (hasNoHeadersFromTemplate) {
    return (
    <p>
    No supported column headers were detected in the uploaded CSV. Please refer to our <a href={DOCS_URL}>formatting guidelines</a>.
    </p>
    )
  }

  if (!hasImportableHeaders) {
    return (
      <p>
        At least one column must provide a way to identify a person on each row. This can be any one of {PIIFields}. Please refer to our <a href={DOCS_URL}>formatting guidelines</a>.
      </p>
    )
  }

  if (hasNoRows) {
    return <p>No rows have been included in the uploaded CSV. There is no contact data to import!'</p>
  }

  if (hasNoImportableRows) {
    return (
      <p>
      No rows can be imported because none were found with valid contact information. Each row must have one of {PIIFields} in order for Clay to create a contact. Please refer to our <a href={DOCS_URL}>formatting guidelines</a>. You can also <a href={errorCSV} download={errorCSVFileName}>download your CSV with error information</a>.
      </p>
    )
  }

  if (errors.row) {
    return (
      <>
      <p>
      <b>{localizeNumber(erroredRowCount)} row{erroredRowCount > 1 ? 's' : ''}</b> {erroredRowCount > 1 ? 'are' : 'is'} missing contact information and cannot be imported. Please <a href={errorCSV} download={errorCSVFileName}>download your CSV with errors appended</a> for further information.
      </p>
      <p>
      The remaining <b>{localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}</b> can still be imported without any further changes, or you can fix the errors shown in the CSV above and resubmit.
      </p>
      </>
    )
  }

  if (warnings.row && warnings.column) {
    return (
      <>
      <p>
      <b>{localizeNumber(warnedRowCount)} row{warnedRowCount > 1 ? 's' : ''}</b> {warnedRowCount > 1 ? 'have' : 'has'} some values that are not formatted correctly. Please <a href={errorCSV} download={errorCSVFileName}>download your CSV with formatting warnings appended</a> for further information.
      </p>
      <p>
      Also, some of the included columns are not supported by Clay. Data found in these columns will be included as a note for each contact created. Please refer to our <a href={DOCS_URL}>formatting guidelines</a> to see all supported column headers.
      </p>
      <p>
      All <b>{localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}</b> can still be imported without any further changes, or you can fix the warnings indicated here and resubmit.
      </p>
      </>
    );
  }

  if (warnings.row) {
    return (
      <>
      <p>
      <b>{localizeNumber(warnedRowCount)} row{warnedRowCount > 1 ? 's' : ''}</b> {warnedRowCount > 1 ? 'have' : 'has'} some values that are not formatted correctly. Please <a href={errorCSV} download={errorCSVFileName}>download your CSV with formatting warnings appended</a> for further information.
      </p>
      <p>
      All <b>{localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}</b> can still be imported without any further changes, or you can fix the warnings indicated shown in the CSV above and resubmit.
      </p>
      </>
    );
  }

  if (warnings.column) {
    return (
      <>
      <p>
      Some of the included columns are not supported by Clay. Data found in these columns will be included as a note for each contact created. Please refer to our <a href={DOCS_URL}>formatting guidelines</a> to see all supported column headers.
      </p>
      <p>
      All <b>{localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}</b> can still be imported without any further changes, or you can remove or fix unsupported column headers and resubmit.
      </p>
      </>
    )
  }

  return <>Please <a href={errorCSV} download={errorCSVFileName}>download your CSV with appended details</a> for further information.</>;
}


const ReviewView = ({
  errors,
  warnings,
  fileName,
  backToUpload,
  hasNoHeadersFromTemplate,
  hasImportableHeaders,
  detectedUnsupportedFields,
  erroredRowCount,
  warnedRowCount,
  importableRowCount,
  hasNoRows,
  hasNoImportableRows,
  downloadableCSV,
  proceedToImportStep,
  close,
  forceDarkMode,
}: {
  errors: GeneralCSVErrors;
  warnings: GeneralCSVWarnings;
  fileName: string;
  backToUpload: () => void;
  hasNoHeadersFromTemplate: boolean;
  hasImportableHeaders: boolean;
  detectedUnsupportedFields: string[];
  erroredRowCount: number;
  warnedRowCount: number;
  importableRowCount: number;
  hasNoRows: boolean;
  hasNoImportableRows: boolean;
  downloadableCSV: string;
  proceedToImportStep: () => void;
  close: () => void;
  forceDarkMode: boolean;
}) => {
  const hasError = errors.row || errors.column;
  const hasWarning = warnings.row || warnings.column;

  const hasRowsError = errors.row || hasNoRows;

  return (
    <>
      <div className={styles.selectedFile}>
        <div className={styles.title}>
          <FileIcon />
          {/* <div> */}
          <div className={styles.fileName}>{fileName.replace('.csv', '')}</div>
          <div className={styles.fileExtension}>.csv</div>
          {/* <span>.csv</span> */}
          {/* </div> */}
        </div>
        <SettingsInlineButton className={styles.reuploadBtn} onClick={backToUpload}>
          Re-upload
        </SettingsInlineButton>
      </div>
      <ul className={styles.checklist}>
        <li className={cn(errors.column && styles.error, warnings.column && styles.warning)}>
          <span className={styles.label}>
            Columns are valid
            {(errors.column || warnings.column) && (
              <Tooltip
                placement="top"
                overlay={
                  <Overlay className={styles.tooltip}>
                    {columnMsg(
                      hasNoHeadersFromTemplate,
                      !hasImportableHeaders,
                      detectedUnsupportedFields,
                    )}
                  </Overlay>
                }
              >
                <CircledIIcon width="13" />
              </Tooltip>
            )}
          </span>
          <StatusIcon hasError={errors.column} hasWarning={warnings.column} />
        </li>

        <li className={cn(hasRowsError && styles.error)}>
          <span className={styles.label}>
            Rows have contact info
            {hasRowsError && (
              <Tooltip
                placement="top"
                overlay={<Overlay className={styles.tooltip}>{rowErrorMsg(hasNoRows, erroredRowCount)}</Overlay>}
              >
                <CircledIIcon width="13" />
              </Tooltip>
            )}
          </span>
          <StatusIcon hasError={hasRowsError} />
        </li>

        <li className={cn(warnings.row && styles.warning)}>
          <span className={styles.label}>
            Values properly formatted
            {warnings.row && (
              <Tooltip
                placement="top"
                overlay={<Overlay className={styles.tooltip}>{rowWarningMsg(warnedRowCount)}</Overlay>}
              >
                <CircledIIcon width="13" />
              </Tooltip>
            )}
          </span>
          <StatusIcon hasWarning={warnings.row} />
        </li>
      </ul>

      {(hasError || hasWarning || hasNoRows) && (
        <AlertBox
          status={hasError ? 'error' : 'warning'}
          title={alertBoxTitle(
            errors,
            warnings,
            hasNoHeadersFromTemplate,
            hasImportableHeaders,
            hasNoRows,
            hasNoImportableRows,
          )}
        >
          {alertBoxMessage(
            errors,
            warnings,
            hasNoHeadersFromTemplate,
            hasImportableHeaders,
            hasNoRows,
            hasNoImportableRows,
            importableRowCount,
            erroredRowCount,
            warnedRowCount,
            downloadableCSV,
            `${fileName.replace('.csv', '')}-errors.csv`, 
          )}
        </AlertBox>
      )}

      <div className={styles.actions}>
        <Action className={styles.modalBtn} onClick={close} secondary forceDarkMode={forceDarkMode}>
          Cancel
        </Action>
        <Action className={styles.modalBtn}
          variant="primary"
          disabled={errors.column || hasNoRows || hasNoImportableRows}
          onClick={proceedToImportStep}
          forceDarkMode={forceDarkMode}
        >
          Upload
        </Action>
      </div>
    </>
  );
};

const ImportView = ({
  close,
  forceDarkMode,
  uploadCSVImport,
  erroredRowCount,
  warnedRowCount,
  importableRowCount,
  goBack,
}: {
  close: () => void;
  forceDarkMode: boolean;
  uploadCSVImport: () => void;
  erroredRowCount: number;
  warnedRowCount: number;
  importableRowCount: number;
  goBack: () => void;
}) => {
  let message: ReactNode = '';

  if (erroredRowCount === 0 && warnedRowCount === 0) {
    message = (
      <p>We can import all {localizeNumber(importableRowCount)} {importableRowCount > 1 ? 'rows' : 'row'} from the provided CSV!</p>
    )
  } else if (erroredRowCount === 0 && warnedRowCount > 0) {
    message = (
      <p>We can import all {localizeNumber(importableRowCount)} {importableRowCount > 1 ? 'rows' : 'row'} from the provided CSV. Are you sure you want to ignore the <b>{localizeNumber(warnedRowCount)} {warnedRowCount > 1 ? 'warnings' : 'warning'}</b> and go ahead with the import?</p>
    )
  } else if (erroredRowCount > 0 && warnedRowCount === 0) {
    message = (
      <p>We can import {localizeNumber(importableRowCount)} {importableRowCount > 1 ? 'rows' : 'row'} from the provided CSV. Are you sure you want to ignore the <b>{localizeNumber(erroredRowCount)} errored {erroredRowCount > 1 ? 'rows' : 'row'}</b> and go ahead with the import?</p>
    )
  } else if (erroredRowCount > 0 && warnedRowCount > 0) {
    message = (
      <p>We can import {localizeNumber(importableRowCount)} {importableRowCount > 1 ? 'rows' : 'row'} from the provided CSV. Are you sure you want to ignore the <b>{localizeNumber(erroredRowCount)} errored {erroredRowCount > 1 ? 'rows' : 'row'}</b> and <b>{localizeNumber(warnedRowCount)} {warnedRowCount > 1 ? 'warnings' : 'warning'}</b> and go ahead with the import?</p>
    )
  }

  return (
    <>
      {message}
      <div className={styles.actions}>
        {(erroredRowCount > 0 || warnedRowCount > 0) && (
          <Action className={styles.modalBtn} onClick={goBack} secondary forceDarkMode={forceDarkMode}>
            <ChevronLeftIcon width="5" />
            Back
          </Action>
        )}
        <Action className={styles.modalBtn} onClick={close} secondary forceDarkMode={forceDarkMode}>
          Cancel
        </Action>
        <Action className={styles.modalBtn} variant="primary" onClick={uploadCSVImport} forceDarkMode={forceDarkMode}>
          Start Import
        </Action>
      </div>
    </>
  );
};

const DoneView = ({ close, forceDarkMode }: { close: () => void; forceDarkMode: boolean }) => {
  const group = useMemo(() => new Group({ title: 'Import - Jan 17, 2025' }), []);

  return (
    <>
      <Header.Plain onClose={close} />
      <div className={styles.importView}>
        <Image
          className={styles.image}
          width={207}
          dark={csvImage}
          light={!forceDarkMode && csvImageLight}
        />

        <h3 className="text-default">You're all set!</h3>

        <p>Your CSV file is being imported, and your people should start appearing shortly.</p>
        <span className="information">View your import's status and people</span>
        <GroupPill group={group} />

        <div className={cn(styles.actions, styles.vert)}>
          <Action className={styles.modalBtn} onClick={close} block forceDarkMode={forceDarkMode}>
            Done
          </Action>
        </div>
      </div>
    </>
  );
};

export default function CSVModal({ onClose, forceDarkMode = false }) {
  // const [rawFile, setRawFile] = React.useState<File>();
  const dispatchToStore = useDispatch();

  const [
    {
      userFile,
      // parsingError,
      sanitizedRecords,
      CSVWithErrorStr,
      erroredRowCount,
      warnedRowCount,
      detectedUnsupportedFields,
      view,
      viewState,
      hasImportableHeaders,
      hasNoHeadersFromTemplate,
      allGroupNames,
    },
    dispatch,
  ] = useReducer(csvModalStateReducer, {
    view: 'upload',
  });

  const [downloadableCSV, setDownloadableCSV] = useState<string>();

  const hasColumnError = !hasImportableHeaders || hasNoHeadersFromTemplate;
  const hasColumnWarning = detectedUnsupportedFields?.length > 0;

  const hasRowError = erroredRowCount > 0;
  const hasRowWarning = warnedRowCount > 0;

  const hasNoRows = !sanitizedRecords || sanitizedRecords.length === 0;
  const hasNoImportableRows = erroredRowCount === sanitizedRecords?.length;

  // const hasError = hasColumnError || hasRowError;
  // const hasWarning = hasColumnWarning || hasRowWarning;

  const uploadCSVImport = () => {
    dispatchToStore(createCSVImport({
      rows: sanitizedRecords,
      group_names: allGroupNames,
    }))

    dispatch(['uploadStarted']);
  };

  useEffect(() => {
    if (!userFile) {
      return void 0;
    }

    if (view !== 'review' || viewState !== 'working') {
      return void 0;
    }

    const abort = new AbortController();

    openAndParseCSVFile(userFile)
      .then((parsed) => {
        const detectedFields = parsed.meta.fields.map(field => field.toLowerCase()).filter(
          (field) => !clayAddedFields.includes(field),
        );
        const detectedUnsupportedFields = detectedFields.filter(
          (field) => !supportedFields.includes(field),
        );
        const hasImportableHeaders = includesLookup(detectedFields);
        let erroredRowCount = 0;
        let warnedRowCount = 0;

        const [importableRows, csvRecordsWithErrors, allGroupNames] = parsed.data.reduce(
          ([_importableRows, _csvRecordsWithErrors, _allGroupNames], record) => {
            const { importable, sanitizedRecord, recordWithErrors, groupNames } = validateRow(record);

            if (!importable) {
              erroredRowCount += 1;
              return [_importableRows, [..._csvRecordsWithErrors, recordWithErrors], _allGroupNames];
            }

            if (recordWithErrors._warnings) {
              warnedRowCount += 1;
            }

            return [
              [..._importableRows, sanitizedRecord],
              [..._csvRecordsWithErrors, recordWithErrors],
              Array.from(new Set([..._allGroupNames, ...groupNames])),
            ];
          },
          [[], [], []],
        );

        const CSVWithErrorStr = Papa.unparse({
          fields: parsed.meta.fields.concat(clayAddedFields),
          data: csvRecordsWithErrors,
        });

        dispatch([
          'parsingComplete',
          {
            sanitizedRecords: importableRows,
            erroredRowCount,
            warnedRowCount,
            detectedUnsupportedFields,
            CSVWithErrorStr,
            hasImportableHeaders,
            hasNoHeadersFromTemplate: detectedFields.length === detectedUnsupportedFields.length,
            allGroupNames,
          },
        ]);
      })
      .catch((e) => {
        console.error(e);
      });

    return () => {
      abort.abort();
    };
  }, [userFile, view, viewState]);

  useEffect(() => {
    if (CSVWithErrorStr) {
      const blob = new Blob([CSVWithErrorStr], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      setDownloadableCSV(url);
    }
  }, [CSVWithErrorStr]);

  return (
    <Modal
      className={cn(styles.csvModal, 'modal-medium', view === 'done' && styles.finalView)}
      forceDarkMode={forceDarkMode}
      centered
      isOpen={true}
      onRequestClose={onClose}
    >
      <StateBasedFadeTransition
        state={String(view === 'done')}
        elementByStateMap={{
          true: (
            <div>
              <DoneView close={onClose} forceDarkMode={forceDarkMode} />
            </div>
          ),
          false: (
            <div>
              <Header className={styles.header} v2>
                <span className={cn(view === 'upload' && styles.active)}>Upload file</span>
                <ChevronRightIcon width="5" strokeWidth="2" color="textPrimary" />
                <span className={cn(view === 'review' && styles.active)}>Review</span>
                <ChevronRightIcon width="5" strokeWidth="2" color="textPrimary" />
                <span className={cn(view === 'import' && styles.active)}>Import</span>
              </Header>

              <StateBasedFadeTransition
                state={view as 'upload' | 'review' | 'import'}
                elementByStateMap={{
                  upload: (
                    <div className={cn(styles.csvContent, 'upload')}>
                      <UploadView
                        selectFile={(file) => dispatch(['fileSelectionComplete', { file }])}
                        closeModal={onClose}
                        upload={() => dispatch(['parsingStarted'])}
                        uploadDisabled={!userFile}
                        forceDarkMode={forceDarkMode}
                      />
                    </div>
                  ),
                  review: (
                    <div className={cn(styles.csvContent, 'review')}>
                      <ReviewView
                        errors={{ row: hasRowError, column: hasColumnError }}
                        warnings={{ row: hasRowWarning, column: hasColumnWarning }}
                        fileName={userFile?.name}
                        backToUpload={() => dispatch(['restart'])}
                        hasNoHeadersFromTemplate={hasNoHeadersFromTemplate}
                        hasImportableHeaders={hasImportableHeaders}
                        detectedUnsupportedFields={detectedUnsupportedFields}
                        erroredRowCount={erroredRowCount}
                        warnedRowCount={warnedRowCount}
                        importableRowCount={sanitizedRecords?.length}
                        hasNoRows={hasNoRows}
                        hasNoImportableRows={hasNoImportableRows}
                        downloadableCSV={downloadableCSV}
                        proceedToImportStep={() => dispatch(['readyToUpload'])}
                        close={onClose}
                        forceDarkMode={forceDarkMode}
                      />
                    </div>
                  ),
                  import: (
                    <div className={cn(styles.csvContent, 'import')}>
                      <ImportView
                        close={onClose}
                        forceDarkMode={forceDarkMode}
                        uploadCSVImport={uploadCSVImport}
                        erroredRowCount={erroredRowCount}
                        warnedRowCount={warnedRowCount}
                        importableRowCount={sanitizedRecords?.length}
                        goBack={() => dispatch(['returnToReview'])}
                      />
                    </div>
                  ),
                }}
              />
            </div>
          ),
        }}
      />
    </Modal>
  );
}
